import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

// App colors for particles
const APP_COLORS = {
    'SD': '#b28c50',
    'FEV': '#4CAF50',
    'FEMS': '#3f6a64',
    'COLD': '#2196F3',
    'PhD': '#9b59b6',
};

const MAX_PARTICLES = 20;
const PARTICLE_DURATION = 1200; // ms

const ConnectionLines = ({
    statusBarHeight = 80,
    appCodes = [],
    spawnEvents = []
}) => {
    const svgRef = useRef(null);
    const [svgDimensions, setSvgDimensions] = useState({ width: 0, height: 0 });
    const [tilePositions, setTilePositions] = useState({});
    const [particles, setParticles] = useState([]);
    const particleIdCounter = useRef(0);
    const animationFrameRef = useRef(null);

    // Update SVG dimensions on mount and resize
    useEffect(() => {
        const updateDimensions = () => {
            if (svgRef.current) {
                const parent = svgRef.current.parentElement;
                setSvgDimensions({
                    width: parent.clientWidth,
                    height: parent.clientHeight,
                });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    // Update tile positions whenever layout changes
    useEffect(() => {
        const updateTilePositions = () => {
            const positions = {};
            appCodes.forEach(appCode => {
                const tile = document.querySelector(`[data-tile="${appCode}"]`);
                if (tile) {
                    const rect = tile.getBoundingClientRect();
                    const container = svgRef.current?.parentElement;
                    if (container) {
                        const containerRect = container.getBoundingClientRect();
                        positions[appCode] = {
                            x: rect.left - containerRect.left + rect.width / 2,
                            y: rect.top - containerRect.top,
                            width: rect.width,
                            height: rect.height,
                            color: APP_COLORS[appCode] || '#5fd4c4'
                        };
                    }
                }
            });
            setTilePositions(positions);
        };

        updateTilePositions();

        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(updateTilePositions);
            const container = svgRef.current?.parentElement;
            if (container) {
                observer.observe(container);
            }
        }

        return () => {
            if (observer) observer.disconnect();
        };
    }, [appCodes]);

    // Listen for spawn events and create particles
    useEffect(() => {
        if (spawnEvents.length === 0) return;

        const lastEvent = spawnEvents[spawnEvents.length - 1];

        const newParticle = {
            id: `particle-${particleIdCounter.current++}`,
            appCode: lastEvent.appCode,
            direction: lastEvent.direction,
            startTime: Date.now(),
            progress: 0,
        };

        setParticles(prev => {
            const updated = [...prev, newParticle];
            return updated.slice(-MAX_PARTICLES);
        });
    }, [spawnEvents]);

    // Animation loop
    useEffect(() => {
        if (particles.length === 0) {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            return;
        }

        const animate = () => {
            const now = Date.now();

            setParticles(prev => {
                return prev
                    .map(p => {
                        const elapsed = now - p.startTime;
                        const progress = Math.min(elapsed / PARTICLE_DURATION, 1.0);
                        return { ...p, progress };
                    })
                    .filter(p => p.progress < 1.0);
            });

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [particles.length]);

    const hasActiveParticles = useCallback((appCode) => {
        return particles.some(p => p.appCode === appCode);
    }, [particles]);

    // Generate SVG filter IDs (stable)
    const filterId = 'particle-glow';

    return (
        <Box sx={{
            position: 'absolute',
            top: '-24px',
            left: 0,
            right: 0,
            height: 'calc(100% + 24px)',
            pointerEvents: 'none',
            zIndex: 1,
        }}>
            <svg
                ref={svgRef}
                width={svgDimensions.width}
                height={svgDimensions.height}
                style={{ position: 'absolute' }}
            >
                <defs>
                    {/* Glow filter for particles */}
                    <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    {/* Gradient definitions for each app line */}
                    {Object.entries(tilePositions).map(([appCode, tile]) => (
                        <linearGradient
                            key={`grad-${appCode}`}
                            id={`line-grad-${appCode}`}
                            x1="0" y1="0" x2="0" y2="1"
                        >
                            <stop offset="0%" stopColor={tile.color} stopOpacity="0.5" />
                            <stop offset="50%" stopColor={tile.color} stopOpacity="0.2" />
                            <stop offset="100%" stopColor={tile.color} stopOpacity="0.5" />
                        </linearGradient>
                    ))}
                </defs>

                {/* Connection lines */}
                {Object.entries(tilePositions).map(([appCode, tile]) => {
                    const lineX = tile.x;
                    const lineY1 = statusBarHeight;
                    const lineY2 = tile.y;
                    const isActive = hasActiveParticles(appCode);

                    return (
                        <g key={`line-${appCode}`}>
                            {/* Background line — always visible, dashed */}
                            <line
                                x1={lineX}
                                y1={lineY1}
                                x2={lineX}
                                y2={lineY2}
                                stroke={`url(#line-grad-${appCode})`}
                                strokeWidth="1"
                                strokeDasharray="4,6"
                                opacity={0.4}
                            />

                            {/* Active glow line — solid, brighter when particles present */}
                            {isActive && (
                                <line
                                    x1={lineX}
                                    y1={lineY1}
                                    x2={lineX}
                                    y2={lineY2}
                                    stroke={tile.color}
                                    strokeWidth="2"
                                    opacity={0.35}
                                    filter={`url(#${filterId})`}
                                />
                            )}
                        </g>
                    );
                })}

                {/* Animated particles */}
                {particles.map(particle => {
                    const tile = tilePositions[particle.appCode];
                    if (!tile) return null;

                    const lineX = tile.x;
                    const lineY1 = statusBarHeight;
                    const lineY2 = tile.y;
                    const distance = lineY2 - lineY1;

                    // Ease-in-out for smoother motion
                    const eased = particle.progress < 0.5
                        ? 2 * particle.progress * particle.progress
                        : 1 - Math.pow(-2 * particle.progress + 2, 2) / 2;

                    let particleY;
                    if (particle.direction === 'up') {
                        particleY = lineY2 - (distance * eased);
                    } else {
                        particleY = lineY1 + (distance * eased);
                    }

                    // Fade out in last 30%
                    const fadeOpacity = particle.progress > 0.7
                        ? 1 - ((particle.progress - 0.7) / 0.3)
                        : 1;

                    // Trail: 3 ghost circles behind the particle
                    const trailCount = 3;
                    const trailSpacing = 0.04;

                    return (
                        <g key={particle.id}>
                            {/* Trail ghosts */}
                            {Array.from({ length: trailCount }).map((_, ti) => {
                                const trailProgress = Math.max(0, particle.progress - (ti + 1) * trailSpacing);
                                const trailEased = trailProgress < 0.5
                                    ? 2 * trailProgress * trailProgress
                                    : 1 - Math.pow(-2 * trailProgress + 2, 2) / 2;
                                let trailY;
                                if (particle.direction === 'up') {
                                    trailY = lineY2 - (distance * trailEased);
                                } else {
                                    trailY = lineY1 + (distance * trailEased);
                                }
                                const trailOpacity = fadeOpacity * (0.3 - ti * 0.08);
                                const trailR = 4 - ti * 0.8;
                                return (
                                    <circle
                                        key={`trail-${ti}`}
                                        cx={lineX}
                                        cy={trailY}
                                        r={trailR}
                                        fill={tile.color}
                                        opacity={trailOpacity}
                                    />
                                );
                            })}

                            {/* Main particle with glow */}
                            <circle
                                cx={lineX}
                                cy={particleY}
                                r="5"
                                fill={tile.color}
                                opacity={fadeOpacity * 0.9}
                                filter={`url(#${filterId})`}
                            />

                            {/* Bright core */}
                            <circle
                                cx={lineX}
                                cy={particleY}
                                r="2"
                                fill="#ffffff"
                                opacity={fadeOpacity * 0.8}
                            />
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
};

export default ConnectionLines;
